import { useState, useEffect, useRef } from "react";
import { useAuth } from "./context/AuthContext";
import AuthModal from "./components/AuthModal";
import Button from "./components/ui/Button";
import ContactDetailsModal, {
  type ContactDetails,
} from "./components/ContactDetailsModal";
import { getAuthService } from "./services/factory";
import { trackAuthGoal } from "./utils/metrika";
import { markAppReady } from "./sk-auth-init";
import "./styles.css";

function App() {
  const [isModalOpen, setIsModalOpen] = useState(
    window.SkAuthConfig?.mode !== "bitrix",
  );
  const [authView, setAuthView] = useState("contact-details");
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(
    null,
  );
  const [modalOptions, setModalOptions] = useState<{ orderId?: number | string | null }>({});
  const { user } = useAuth();

  const wasModalOpenRef = useRef(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModalWithView = (view: string, options?: { orderId?: number | string | null }) => {
    setAuthView(view);
    setModalOptions(options || {});
    setIsModalOpen(true);
  };

  const reloadBitrixPage = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("_auth_refresh", String(Date.now()));
    window.location.href = url.toString();
  };

  // Перезагрузка страницы после успешной авторизации/регистрации,
  // если в момент авторизации модалка была открыта
  useEffect(() => {
    if (authView === "contact-details") return;

    if (isModalOpen) {
      wasModalOpenRef.current = true;
    }

    if (!isModalOpen && wasModalOpenRef.current && user) {
      wasModalOpenRef.current = false;

      // Небольшая задержка перед перезагрузкой страницы, чтобы события аналитики
      // (Яндекс.Метрика, dataLayer) гарантированно успели отправиться по сети
      const timer = setTimeout(() => {
        reloadBitrixPage();
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [isModalOpen, user, authView]);

  // Обработка глобальных событий для открытия/закрытия модального окна
  useEffect(() => {
    const handleOpen = (event: CustomEvent) => {
      const view = event.detail?.view || "identifier";
      const options = event.detail?.options;
      openModalWithView(view, options);
    };

    const handleClose = () => {
      closeModal();
    };

    window.addEventListener("sk-auth:open", handleOpen as EventListener);
    window.addEventListener("sk-auth:close", handleClose);

    markAppReady();

    return () => {
      window.removeEventListener("sk-auth:open", handleOpen as EventListener);
      window.removeEventListener("sk-auth:close", handleClose);
    };
  }, []);

  const isBitrixMode =
    typeof window !== "undefined" && window.SkAuthConfig?.mode === "bitrix";

  const modal =
    isModalOpen &&
    (authView === "contact-details" ? (
      <ContactDetailsModal
        orderId={modalOptions.orderId}
        onClose={closeModal}
        onSubmit={async (details, orderId) => {
          const authService = getAuthService();
          const result = await authService.saveContactDetails(
            details.fullName,
            details.email,
            orderId,
          );
          if (!result.success) {
            throw new Error(
              result.errors?.[0] || "Не удалось сохранить контактные данные",
            );
          }
          setContactDetails(details);
          trackAuthGoal("CONTACT_DETAILS_SUCCESS", {
            email: details.email,
            orderId: orderId || undefined,
          });
          window.dispatchEvent(
            new CustomEvent("sk-auth:contact-details", {
              detail: { ...details, user: result.user, orderId },
            }),
          );
          closeModal();
        }}
      />
    ) : (
      <AuthModal onClose={closeModal} initialView={authView} />
    ));

  if (isBitrixMode) {
    return <>{modal}</>;
  }

  return (
    <div className="auth-app">
      <h1>Северная Каролина</h1>
      <p>Контактные данные для заказа</p>
      <Button onClick={() => openModalWithView("contact-details")}>
        Указать ФИО и email
      </Button>
      {contactDetails && (
        <p role="status">
          Данные заполнены. В режиме предпросмотра они не отправляются на
          сервер.
        </p>
      )}

      {modal}
    </div>
  );
}

export default App;
